import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, Loader2, Wifi, WifiOff, ShieldCheck, ShieldOff, IndianRupee, Package, Star, Phone, Mail, Bike, Calendar, AlertCircle, CheckCircle2, Activity, Wallet, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function AdminAgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, o, e] = await Promise.all([
        api.get(`/admin/agents/${agentId}`),
        api.get(`/admin/agents/${agentId}/orders?limit=50`),
        api.get(`/admin/agents/${agentId}/earnings?limit=50`),
      ]);
      setProfile(p.data);
      setOrders(o.data);
      setEarnings(e.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load agent");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [agentId]);

  const handleApprove = async (verify) => {
    setActionLoading(true);
    try {
      await api.put(`/admin/agents/${agentId}/approve`, { is_verified: verify });
      toast.success(verify ? "Agent approved" : "Agent suspended");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleOnline = async () => {
    setActionLoading(true);
    try {
      await api.put(`/admin/agents/${agentId}/toggle-status`, { is_online: !profile.agent.is_online });
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="agent-detail-loading">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );
  if (error) return (
    <div className="p-10 text-center" data-testid="agent-detail-error">
      <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
      <p className="text-base font-semibold text-slate-700">Couldn't load agent</p>
      <p className="text-sm text-slate-400 mt-1">{error}</p>
      <Button onClick={() => navigate(-1)} className="mt-4 rounded-xl"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
    </div>
  );

  const { agent, stats } = profile;
  const orFallback = (v) => (v === undefined || v === null || v === "" ? "N/A" : v);

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="agent-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/dashboard/agents")} className="rounded-xl" data-testid="agent-detail-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Agents
          </Button>
          <div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="agent-detail-name">{agent.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5">Agent profile & performance</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {agent.is_verified ? (
            <Button variant="outline" onClick={() => handleApprove(false)} disabled={actionLoading} className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid="agent-detail-suspend-btn">
              <ShieldOff className="h-4 w-4 mr-1" /> Suspend
            </Button>
          ) : (
            <Button onClick={() => handleApprove(true)} disabled={actionLoading} className="rounded-xl bg-green-600 hover:bg-green-700 text-white" data-testid="agent-detail-approve-btn">
              <ShieldCheck className="h-4 w-4 mr-1" /> Approve
            </Button>
          )}
          <Button variant="outline" onClick={handleToggleOnline} disabled={actionLoading} className="rounded-xl" data-testid="agent-detail-toggle-btn">
            {agent.is_online ? <><WifiOff className="h-4 w-4 mr-1" /> Set Offline</> : <><Wifi className="h-4 w-4 mr-1" /> Set Online</>}
          </Button>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="agent-profile-card">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center shrink-0">
            <span className="text-[#FF6B00] text-2xl font-bold font-['Outfit']">{(agent.name || "A")[0].toUpperCase()}</span>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <InfoRow icon={Mail} label="Email" value={orFallback(agent.email)} />
            <InfoRow icon={Phone} label="Phone" value={orFallback(agent.phone)} />
            <InfoRow icon={Calendar} label="Joined" value={agent.created_at ? new Date(agent.created_at).toLocaleDateString() : "N/A"} />
            <InfoRow icon={Bike} label="Vehicle" value={orFallback(agent.vehicle_type ? `${agent.vehicle_type}${agent.vehicle_number ? ` (${agent.vehicle_number})` : ""}` : agent.vehicle_number)} />
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-slate-400" />
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wider">Status</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {agent.is_online
                    ? <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium"><Wifi className="h-3 w-3" /> Online</span>
                    : <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full text-xs font-medium"><WifiOff className="h-3 w-3" /> Offline</span>
                  }
                  {agent.is_verified
                    ? <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle2 className="h-3 w-3" /> Verified</span>
                    : <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium"><AlertCircle className="h-3 w-3" /> Pending</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-400" />
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wider">Rating</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                  {stats.avg_rating > 0 ? `${stats.avg_rating} / 5` : "N/A"}
                  {stats.total_ratings > 0 && <span className="text-[10px] text-slate-400 ml-1">({stats.total_ratings})</span>}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Package} label="Assigned Total" value={stats.assigned_total} color="blue" testid="kpi-assigned" />
        <KpiCard icon={CheckCircle2} label="Completed" value={stats.completed_pickups} color="green" testid="kpi-completed" />
        <KpiCard icon={Activity} label="Active" value={stats.active_orders} color="orange" testid="kpi-active" />
        <KpiCard icon={AlertCircle} label="Cancelled" value={stats.cancelled_orders} color="red" testid="kpi-cancelled" />
        <KpiCard icon={IndianRupee} label="Total Earnings" value={`₹${stats.total_earnings}`} color="emerald" testid="kpi-earnings" />
        <KpiCard icon={Wallet} label="Available Balance" value={`₹${stats.available_balance}`} color="emerald" testid="kpi-available" />
        <KpiCard icon={TrendingUp} label="Pending Payout" value={`₹${stats.pending_payout}`} color="amber" testid="kpi-pending-payout" />
        <KpiCard icon={CheckCircle2} label="Paid Out" value={`₹${stats.total_paid_payout}`} color="slate" testid="kpi-paid-payout" />
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="agent-orders-section">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Orders Handled</h2>
            <p className="text-xs text-slate-400 mt-0.5">{orders.length} orders</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/dashboard/orders")} className="rounded-xl text-xs" data-testid="agent-view-all-orders">
            View All Orders
          </Button>
        </div>
        {orders.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No orders yet</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6">Tracking</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Item</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.slice(0, 25).map((o) => (
                  <TableRow key={o.tracking_id} className="border-slate-50 row-hover" data-testid={`agent-order-${o.tracking_id}`}>
                    <TableCell className="pl-6 py-3"><span className="font-mono text-sm font-semibold text-slate-900">{o.tracking_id}</span></TableCell>
                    <TableCell className="py-3"><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="py-3 text-sm text-slate-700">{orFallback(o.package_description)}</TableCell>
                    <TableCell className="py-3 text-sm text-slate-700"><IndianRupee className="h-3 w-3 inline" />{(o.price?.final_price ?? o.price?.total) || "—"}</TableCell>
                    <TableCell className="py-3 text-xs text-slate-500">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Earnings History */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="agent-earnings-section">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Earnings History</h2>
          <p className="text-xs text-slate-400 mt-0.5">{earnings.length} entries</p>
        </div>
        {earnings.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No earnings recorded</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6">Order</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Model</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order Total</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Earned</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {earnings.slice(0, 25).map((e) => (
                  <TableRow key={e.tracking_id} className="border-slate-50 row-hover" data-testid={`agent-earning-${e.tracking_id}`}>
                    <TableCell className="pl-6 py-3"><span className="font-mono text-sm font-semibold text-slate-900">{e.tracking_id}</span></TableCell>
                    <TableCell className="py-3">
                      {e.commission_model === "gig" && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">GIG</span>}
                      {e.commission_model === "legacy_80pct" && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">LEGACY</span>}
                      {!e.commission_model && <span className="text-[11px] text-slate-400">N/A</span>}
                    </TableCell>
                    <TableCell className="py-3 text-sm text-slate-500"><IndianRupee className="h-3 w-3 inline" />{e.order_total || "—"}</TableCell>
                    <TableCell className="py-3 text-sm font-bold text-green-600"><IndianRupee className="h-3 w-3 inline" />{e.amount}</TableCell>
                    <TableCell className="py-3 text-xs text-slate-500">{e.created_at ? new Date(e.created_at).toLocaleDateString() : "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-slate-900 truncate">{value}</p>
      </div>
    </div>
  );
}

const kpiTone = {
  blue: "bg-blue-50 text-blue-700",
  green: "bg-green-50 text-green-700",
  orange: "bg-orange-50 text-[#FF6B00]",
  red: "bg-red-50 text-red-600",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  slate: "bg-slate-100 text-slate-600",
};

function KpiCard({ icon: Icon, label, value, color = "slate", testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 card-hover" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${kpiTone[color]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="font-['Outfit'] text-xl font-bold text-slate-900 tracking-tight">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
